import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UploadFileForm } from '@/components/forms/upload-file-form/upload-file-form';
import { SelectOffice } from '@/components/select-office/select-office';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { TONES } from '@/lib/tones';
import { cn } from '@/lib/utils';
import { SelectSearchForm } from '../select-search/select-search-form';
import { useImportAccountingDoctorAttendingCsvFormViewModel } from './import-accounting-doctor-attending-csv-form.viewmodel';
import { ImportAccountingDoctorAttendingCsvUseForm } from './use-import-accounting-doctor-attending-csv-form';

type Props = {
  form: ImportAccountingDoctorAttendingCsvUseForm;
};

const ImportAccountingDoctorAttendingCsvForm = ({ form }: Props) => {
  const { t } = useTranslation();

  const viewModel = useImportAccountingDoctorAttendingCsvFormViewModel({
    form,
  });

  const [doctorSelectOpen, setDoctorSelectOpen] = useState(false);

  useEffect(() => {
    if (!viewModel.officeId) {
      return;
    }
    const currentDoctor = form.getValues('doctorAttending');
    if (currentDoctor) {
      return;
    }
    // Defer past Radix Select's auto-focus-back-to-trigger, otherwise
    // the doctor popover opens then immediately closes on focus-outside.
    const id = window.setTimeout(() => setDoctorSelectOpen(true), 100);
    return () => window.clearTimeout(id);
  }, [viewModel.officeId, form]);

  const csvFileFormState: ReactNode = (() => {
    const csvFielState = viewModel.formState.doctolibAccountingFile;
    if (csvFielState) {
      if (csvFielState.type === 'warning') {
        return (
          <em className={cn('font-medium text-sm', TONES.orange.text)}>
            {t(csvFielState.message)}
          </em>
        );
      }

      if (csvFielState.type === 'error') {
        return (
          <em className="font-medium text-destructive text-sm">
            {t(csvFielState.message)}
          </em>
        );
      }
    }

    return null;
  })();

  return (
    <Form {...form}>
      <form className="grid gap-4">
        {/* Office */}
        <FormField
          control={form.control}
          name="officeId"
          render={() => (
            <FormItem>
              <div className="flex flex-col gap-2">
                <FormLabel>
                  {t(
                    'import-accounting-doctor-attending-csv-form.office.label',
                  )}
                </FormLabel>

                <SelectOffice
                  width="w-full"
                  placeholder={t(
                    'import-accounting-doctor-attending-csv-form.office.placeholder',
                  )}
                  onValueChange={(office) => {
                    const prevOfficeId = form.getValues('officeId');
                    const nextOfficeId = office?.id as string;
                    form.setValue('officeId', nextOfficeId);
                    if (prevOfficeId === nextOfficeId) {
                      return;
                    }
                    form.setValue('doctorAttending', null as never, {
                      shouldDirty: true,
                    });
                  }}
                />
              </div>

              <FormMessage />
            </FormItem>
          )}
        />

        {/* Doctor attending */}
        <SelectSearchForm
          form={form}
          fieldName="doctorAttending"
          disabled={!viewModel.officeId}
          label={t(
            'import-accounting-doctor-attending-csv-form.doctor-attending.label',
          )}
          placeholder={t(
            'import-accounting-doctor-attending-csv-form.doctor-attending.placeholder',
          )}
          searchPlaceholder={t(
            'import-accounting-doctor-attending-csv-form.doctor-attending.search.placeholder',
          )}
          searchEmpty={t(
            'import-accounting-doctor-attending-csv-form.doctor-attending.search.empty',
          )}
          options={viewModel.doctorAttendings.options}
          getValuePlaceholder={
            viewModel.doctorAttendings.api.getValuePlaceholder
          }
          onSearchChange={viewModel.doctorAttendings.api.setSearch}
          open={doctorSelectOpen}
          onOpenChange={setDoctorSelectOpen}
        />

        <FormField
          control={form.control}
          name="doctolibAccountingFile"
          render={() => (
            <FormItem className="grid gap-2">
              <UploadFileForm
                label="import-accounting-doctor-attending-csv-form.import-csv.label"
                description="import-accounting-doctor-attending-csv-form.import-csv.description"
                btnLabel="import-accounting-doctor-attending-csv-form.import-csv.btn-import"
                accept=".csv"
                onUpload={({ files }) => {
                  const [file] = files;
                  form.setValue('doctolibAccountingFile', file, {
                    shouldDirty: true,
                    shouldTouch: true,
                    shouldValidate: true,
                  });
                  return undefined;
                }}
              />

              <FormMessage />

              {csvFileFormState}
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
};

export default ImportAccountingDoctorAttendingCsvForm;
